import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, RefreshCw, Search, Settings } from 'lucide-react';
import { apiClient } from '../../../api/apiClient';
import { useBodyScrollLock } from '../../../hooks/useBodyScrollLock';
import {
  DetailModal,
  MobileCard,
  STATUS_OPTIONS,
  Summary,
} from './ContractRenewalParts';
import ContractRenewalCalendar from './ContractRenewalCalendar';
import ContractRenewalSettingsModals from './ContractRenewalSettingsModals';
import ContractRenewalTable from './ContractRenewalTable';
import CompanyFilterChips from '../integrated/CompanyFilterChips';
import {
  inputStyle,
  pageStyle,
  summaryGridStyle,
} from './styles';
import {
  stdBlackBtn,
  stdHeaderRow,
  stdOutlineBtn,
  stdPageSubtitle,
  stdPageTitle,
  stdSearchIcon,
  stdSearchInput,
  stdSearchWrap,
  stdTabBtn,
  stdTabGroup,
} from '../adminlayout/designStandard';

// 표준 버튼에 lucide 아이콘을 넣을 때 정렬용 (스타일만, 토큰 색·모양 유지)
const iconBtnAlign = { display: 'inline-flex', alignItems: 'center', gap: 6 };

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return isMobile;
}

function normalizeCompanyCode(value) {
  return String(value || 'PINE').trim().toUpperCase() || 'PINE';
}

function defaultApiBase(companyCode, integratedMenuId) {
  if (integratedMenuId) return `/api/admin/integrated-menus/${integratedMenuId}/contract-renewals`;
  const code = normalizeCompanyCode(companyCode);
  return code === 'PINE'
    ? '/api/admin/employee-contract-renewals'
    : `/api/admin/ext/${code}/employee-contract-renewals`;
}

function periodLabel(value, unit) {
  const amount = Number(value || 0);
  if (amount <= 0) return '3개월';
  const label = unit === 'year' ? '년' : unit === 'day' ? '일' : '개월';
  return `${amount}${label}`;
}

export default function ContractRenewalManagement({
  companyCode = 'PINE',
  pageTitle = '계약갱신관리',
  apiBase,
  integratedMenuId = null,
  companyCodes = [],
  selectedCompanyCodes,
  setSelectedCompanyCodes,
}) {
  const isMobile = useIsMobile();
  const normalizedCompanyCode = useMemo(() => normalizeCompanyCode(companyCode), [companyCode]);
  const normalizedCompanyCodes = useMemo(
    () => companyCodes.map(normalizeCompanyCode).filter(Boolean),
    [companyCodes]
  );
  const activeCompanyCodes = useMemo(() => {
    if (!integratedMenuId) return [normalizedCompanyCode];
    const selected = Array.isArray(selectedCompanyCodes) && selectedCompanyCodes.length > 0
      ? selectedCompanyCodes.map(normalizeCompanyCode).filter((code) => normalizedCompanyCodes.includes(code))
      : normalizedCompanyCodes;
    return selected.length > 0 ? selected : normalizedCompanyCodes;
  }, [integratedMenuId, normalizedCompanyCode, normalizedCompanyCodes, selectedCompanyCodes]);
  const resolvedApiBase = useMemo(
    () => apiBase || defaultApiBase(normalizedCompanyCode, integratedMenuId),
    [apiBase, integratedMenuId, normalizedCompanyCode]
  );
  const canManageSettings = true;
  const defaultSetupCompanyCode = activeCompanyCodes[0] || normalizedCompanyCode;
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [includeResigned, setIncludeResigned] = useState(false);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [message, setMessage] = useState('');
  const [detail, setDetail] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [setupCompanyCode, setSetupCompanyCode] = useState(defaultSetupCompanyCode);
  const [memoDrafts, setMemoDrafts] = useState({});

  useBodyScrollLock(canManageSettings && (manualOpen || settingsOpen));

  useEffect(() => {
    setSetupCompanyCode((prev) => (
      activeCompanyCodes.includes(prev) ? prev : defaultSetupCompanyCode
    ));
  }, [activeCompanyCodes, defaultSetupCompanyCode]);

  const query = useMemo(() => {
    const params = new URLSearchParams({ status, include_resigned: String(includeResigned) });
    if (search.trim()) params.set('q', search.trim());
    if (integratedMenuId && activeCompanyCodes.length > 0) params.set('company_codes', activeCompanyCodes.join(','));
    return params.toString();
  }, [activeCompanyCodes, includeResigned, integratedMenuId, search, status]);

  const endpoint = useCallback((path = '', extra = {}) => {
    const params = new URLSearchParams(extra);
    const suffix = params.toString();
    return `${resolvedApiBase}${path}${suffix ? `?${suffix}` : ''}`;
  }, [resolvedApiBase]);

  const bodyWithCompany = useCallback((body = {}, code = normalizedCompanyCode) => {
    if (!integratedMenuId) return body;
    return { ...body, company_code: normalizeCompanyCode(code) };
  }, [integratedMenuId, normalizedCompanyCode]);

  const loadRows = useCallback(async () => {
    setLoading(true);
    setMessage('');
    const res = await apiClient.get(`${resolvedApiBase}${query ? `?${query}` : ''}`);
    setLoading(false);
    if (!res.ok || !res.data?.success) {
      setRows([]);
      setSummary({});
      setMessage(res.data?.message || '계약갱신관리 목록을 불러오지 못했습니다.');
      return;
    }
    setRows(res.data.data?.rows || []);
    setSummary(res.data.data?.summary || {});
  }, [query, resolvedApiBase]);

  useEffect(() => { loadRows(); }, [loadRows]);

  async function openDetail(row) {
    const res = await apiClient.get(endpoint(`/${row.user_id}`, integratedMenuId ? { company_code: row.company_code } : {}));
    if (!res.ok || !res.data?.success) {
      setMessage(res.data?.message || '상세 이력을 불러오지 못했습니다.');
      return;
    }
    setDetail(res.data.data);
    setMemoDrafts({});
  }

  async function reloadDetail(userId, code = detail?.user?.company_code || normalizedCompanyCode) {
    if (!userId) return;
    const res = await apiClient.get(endpoint(`/${userId}`, integratedMenuId ? { company_code: code } : {}));
    if (res.ok && res.data?.success) setDetail(res.data.data);
  }

  async function runAction(row, action) {
    const isRenew = action === 'renew';
    const renewalLabel = periodLabel(row.enrollment?.renewal_period_value || 3, row.enrollment?.renewal_period_unit || 'month');
    const text = isRenew
      ? `${row.name}님의 현재 계약을 갱신하고 다음 ${renewalLabel} 계약 구간을 만들까요?`
      : `${row.name}님의 현재 계약을 종료로 표시할까요?\n직원관리 퇴사일은 자동으로 바뀌지 않습니다.`;
    if (!window.confirm(text)) return;
    setSavingId(row.user_id);
    setMessage('');
    const res = await apiClient.post(endpoint(`/${row.user_id}/${action}`), bodyWithCompany({}, row.company_code));
    setSavingId(null);
    if (!res.ok || !res.data?.success) {
      setMessage(res.data?.message || '저장에 실패했습니다.');
      return;
    }
    setMessage(isRenew ? '계약 갱신을 저장했습니다.' : '계약 종료를 저장했습니다.');
    await loadRows();
    await reloadDetail(row.user_id, row.company_code);
  }

  async function saveMemo(termId) {
    setSavingId(termId);
    const res = await apiClient.patch(endpoint(`/terms/${termId}`), bodyWithCompany({
      memo: memoDrafts[termId] ?? '',
    }, detail?.user?.company_code));
    setSavingId(null);
    if (!res.ok || !res.data?.success) {
      setMessage(res.data?.message || '비고 저장에 실패했습니다.');
      return;
    }
    setMessage('비고를 저장했습니다.');
    setDetail(res.data.data);
    setMemoDrafts({});
    await loadRows();
  }

  async function undoLast(user) {
    if (!user?.user_id) return;
    if (!window.confirm(`${user.name}님의 최근 갱신/종료 작업을 되돌릴까요?`)) return;
    setSavingId(`undo-${user.user_id}`);
    setMessage('');
    const res = await apiClient.post(endpoint(`/${user.user_id}/undo-last`), bodyWithCompany({}, user.company_code));
    setSavingId(null);
    if (!res.ok || !res.data?.success) {
      setMessage(res.data?.message || '최근 작업 되돌리기에 실패했습니다.');
      return;
    }
    setMessage('최근 작업을 되돌렸습니다.');
    setDetail(res.data.data);
    setMemoDrafts({});
    await loadRows();
  }

  async function deleteManual(row) {
    if (!window.confirm(`${row.name}님을 계약갱신관리 대상에서 삭제할까요?\n직접 등록으로 만든 미결정 구간도 함께 삭제됩니다.`)) return;
    setSavingId(row.user_id);
    setMessage('');
    const res = await apiClient.delete(endpoint(`/enrollments/${row.user_id}`, integratedMenuId ? { company_code: row.company_code } : {}));
    setSavingId(null);
    if (!res.ok || !res.data?.success) {
      setMessage(res.data?.message || '직접 등록 삭제에 실패했습니다.');
      return;
    }
    setMessage(`${row.name}님 직접 등록을 삭제했습니다.`);
    if (detail?.user?.user_id === row.user_id) setDetail(null);
    await loadRows();
  }

  const handlers = {
    onDetail: openDetail,
    onRenew: (row) => runAction(row, 'renew'),
    onEnd: (row) => runAction(row, 'end'),
    onDelete: deleteManual,
  };

  const setupCompanyOptions = integratedMenuId ? normalizedCompanyCodes : [];
  const settingsCompanyCode = integratedMenuId ? setupCompanyCode : '';
  const calendarCompanyCode = integratedMenuId ? defaultSetupCompanyCode : normalizedCompanyCode;

  function openManualSettings() {
    setSetupCompanyCode(defaultSetupCompanyCode);
    setManualOpen(true);
  }

  function openDepartmentSettings() {
    setSetupCompanyCode(defaultSetupCompanyCode);
    setSettingsOpen(true);
  }

  const companySelector = integratedMenuId && setupCompanyOptions.length > 1 && (
    <label style={{ display: 'grid', gap: 5, marginBottom: 12, color: 'var(--text-sub)', fontSize: 12, fontWeight: 850 }}>
      <span>적용 회사</span>
      <select
        style={{ ...inputStyle, maxWidth: 180 }}
        value={setupCompanyCode}
        onChange={(event) => setSetupCompanyCode(normalizeCompanyCode(event.target.value))}
      >
        {setupCompanyOptions.map((code) => (
          <option key={code} value={code}>{code}</option>
        ))}
      </select>
    </label>
  );

  return (
    <div style={pageStyle}>
      <div style={stdHeaderRow(isMobile)}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <h2 style={stdPageTitle}>{pageTitle}</h2>
            {integratedMenuId && normalizedCompanyCodes.length > 0 && setSelectedCompanyCodes && (
              <CompanyFilterChips
                companyCodes={normalizedCompanyCodes}
                selected={activeCompanyCodes}
                onChange={setSelectedCompanyCodes}
              />
            )}
          </div>
          <div style={stdPageSubtitle}>직원 계약 갱신 일정을 관리합니다.</div>
        </div>
        <div style={{ display: 'inline-flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {canManageSettings && <button type="button" style={{ ...stdOutlineBtn, ...iconBtnAlign }} onClick={openDepartmentSettings}>
            <Settings size={15} /> 자동등록 설정
          </button>}
          <button
            type="button"
            style={{ ...stdOutlineBtn, ...iconBtnAlign, opacity: loading ? 0.65 : 1, cursor: loading ? 'wait' : 'pointer' }}
            onClick={loadRows}
            disabled={loading}
          >
            <RefreshCw size={15} /> 새로고침
          </button>
          {canManageSettings && <button type="button" style={{ ...stdBlackBtn, ...iconBtnAlign }} onClick={openManualSettings}>
            <Plus size={15} /> 직접 등록
          </button>}
        </div>
      </div>

      <ContractRenewalCalendar
        rows={rows}
        onSelectRow={openDetail}
        isMobile={isMobile}
        apiBase={resolvedApiBase}
        companyCode={calendarCompanyCode}
      />

      <div style={summaryGridStyle}>
        <Summary label="전체" value={summary.total || 0} />
        <Summary label="수습검토" value={summary.probation || 0} tone="#dc2626" />
        <Summary label="계약임박" value={summary.contract_due || 0} tone="#b45309" />
        <Summary label="여유" value={summary.ok || 0} tone="#15803d" />
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ ...stdSearchWrap(isMobile), marginBottom: 0, flex: isMobile ? '1 1 100%' : '0 1 260px' }}>
          <Search size={15} style={stdSearchIcon} />
          <input style={stdSearchInput} value={search} onChange={(event) => setSearch(event.target.value)} placeholder="이름 또는 아이디" />
        </div>
        <select style={{ ...inputStyle, height: 38, borderRadius: 12 }} value={status} onChange={(event) => setStatus(event.target.value)}>
          {STATUS_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
        <div style={stdTabGroup}>
          <button type="button" style={stdTabBtn(!includeResigned)} onClick={() => setIncludeResigned(false)}>
            재직중
          </button>
          <button type="button" style={stdTabBtn(includeResigned)} onClick={() => setIncludeResigned(true)}>
            전체(퇴사 포함)
          </button>
        </div>
      </div>

      {message && <div style={{ color: 'var(--text)', fontWeight: 800 }}>{message}</div>}

      {isMobile ? (
        <div style={{ display: 'grid', gap: 8 }}>
          {rows.map((row) => <MobileCard key={row.user_id} row={row} saving={savingId === row.user_id} handlers={handlers} />)}
        </div>
      ) : (
        <ContractRenewalTable rows={rows} savingId={savingId} handlers={handlers} />
      )}

      <DetailModal
        detail={detail}
        memoDrafts={memoDrafts}
        saving={Boolean(savingId)}
        onClose={() => setDetail(null)}
        onMemo={(termId, value) => setMemoDrafts((prev) => ({ ...prev, [termId]: value }))}
        onSaveMemo={saveMemo}
        onRenew={(row) => runAction(row, 'renew')}
        onEnd={(row) => runAction(row, 'end')}
        onUndo={undoLast}
      />

      {canManageSettings && (
        <ContractRenewalSettingsModals
          manualOpen={manualOpen}
          settingsOpen={settingsOpen}
          apiBase={resolvedApiBase}
          companyCode={settingsCompanyCode}
          companySelector={companySelector}
          onCloseManual={() => setManualOpen(false)}
          onCloseSettings={() => setSettingsOpen(false)}
          onChanged={loadRows}
          setMessage={setMessage}
        />
      )}
    </div>
  );
}
